import { CompositeFilterDescriptor, State } from '@progress/kendo-data-query';

import { Component, OnInit, ViewChild, ViewEncapsulation, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { SelectEvent, StepperActivateEvent, TabStripComponent } from '@progress/kendo-angular-layout';
import { PatientMergeSelectorComponent } from './patient-merge-selector/patient-merge-selector.component';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';
import { cloneDeep } from 'lodash';
import { MemberService } from 'src/app/core/service/member.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { GridViewManager } from 'src/app/core/model/grid-view-manager';
import { DataStateChangeEvent, GridDataResult } from '@progress/kendo-angular-grid';
import { IntlService } from '@progress/kendo-angular-intl';
import { AppConfigService } from 'src/app/core/service/app-config.service';
import { SettingService } from 'src/app/core/service/setting.service';
import { PatientMergeHistoryListService } from 'src/app/core/service/grid-data/grid-data-patient-merge-history.service';
import { Subscription } from 'rxjs';
import { SearchHelperService } from 'src/app/core/utility/search-helper.service';

@Component({
  standalone: false,
  selector: 'app-patient-merge',
  templateUrl: './patient-merge.component.html',
  // styleUrls: ['./patient-merge.component.scss']
  encapsulation: ViewEncapsulation.None,
  styles: [`
  .k-stepper .k-step-label .k-step-text {
    min-width: 200px;
        white-space: nowrap;
     overflow: hidden;
    text-overflow: ellipsis;
  }
  .k-stepper .k-step-label {
    display: inline-block;
  }
  .exchange-patient-selection {
    color: #527186;
    position: absolute;
    left: -10px;
    top: 60px;
  }
  .exchange-patient-selection:hover {
    color: #455f70;
  }
  `]
})
export class PatientMergeComponent implements OnInit, OnDestroy
{
  @ViewChild('sourceSelector') public sourceSelector: PatientMergeSelectorComponent;
  @ViewChild('targetSelector') public targetSelector: PatientMergeSelectorComponent;
  @ViewChild('tabstrip') public tabstrip: TabStripComponent;

  public pageSizes = [5, 10, 25, 50, 100];
  public selectedTab = 0;
  public duplicatedMemberCount = 0;
  public selectedMemberKeys: number[] = [];
  public disabledToggleOnView = false;
  public currentStep = 0;
  public selectDefaultSubject = null;
  public selectSourceSubject = null;
  public selectSourceOperator: 'startsWith' | 'contains' = 'startsWith';
  public selectTargetSubject = null;
  public selectTargetOperator: 'startsWith' | 'contains' = 'startsWith';
  public merging = false;
  public cancelDialogOpened = false;
  public mergeConfirmed = false;
  public patientLabel = {};
  public patientLabelString: string;
  public inverting = false;
  public viewDuplicatedMembers: GridViewManager;
  public hasOpenBatch = false;

  public steps = [
    { label: 'Select Source', icon: 'search' },
    { label: 'Select Target', icon: 'search' },
    { label: 'Review', icon: 'eye' },
    { label: 'Confirm', icon: 'check' },
  ];

  public historyState: State = {
    skip: 0,
    // Initial filter descriptor
    filter: {
      logic: 'and',
      filters: []
    },
    sort: []
  };

  public historyGridView: GridDataResult;
  public historyGridData: any = {};
  public historyCount: number;
  public enableAdvanced = false;
  public filterText = '';

  private listSubscription: Subscription;

  constructor(
    private settingService: SettingService,
    private translate: TranslateService,
    private toastrService: ToastrService,
    private memberService: MemberService,
    private messageHandlerService: MessageHandlerService,
    private router: Router,
    private appConfigService: AppConfigService,
    private patientMergeHistoryListService: PatientMergeHistoryListService,
    private searchHelperService: SearchHelperService,
    public intl: IntlService
  )
  {
    this.viewDuplicatedMembers = new GridViewManager(this.appConfigService.getConfig().pageSize);
  }

  /**
   * Provide loading property from Service.
   */
  public get loadingMergeHistory(): boolean
  {
    return this.patientMergeHistoryListService ? this.patientMergeHistoryListService.loading : false;
  }

  public ngOnInit(): void
  {
    this.selectedMemberKeys = [];
    this.loadLocalizations();

    this.listSubscription = this.patientMergeHistoryListService.subscribe({
      next: (value) =>
      {
        if (value !== null)
        {
          value.data.forEach(d =>
          {
            d.createdOn = new Date(d.createdOn);
            d.sourceMemberFullName = d.sourceMember.fullName;
            d.targetMemberFullName = d.targetMember.fullName;
            if (d.sourceMember.dateOfBirth !== null && d.sourceMember.dateOfBirth !== undefined)
            {
              d.sourceMemberDateOfBirth = new Date(`${d.sourceMember.dateOfBirth}T12:00:00.000`);
            }
            if (d.targetMember.dateOfBirth !== null && d.targetMember.dateOfBirth !== undefined)
            {
              d.targetMemberDateOfBirth = new Date(`${d.targetMember.dateOfBirth}T12:00:00.000`);
            }
          });
        }
        this.historyGridView = {
          data: (value) ? this.historyGridData = value.data : this.historyGridData = [],
          total: (value) ? this.historyCount = value.total : this.historyCount = 0
        };
      }
    });
  }

  public ngOnDestroy(): void
  {
    if (this.listSubscription !== undefined) { this.listSubscription.unsubscribe(); }
  }

  /**
   * Even occur when attempting to select the next step.
   *
   * @param event Event used to prevent selecting the next step for some context.
   */
  public activateStep(event: StepperActivateEvent)
  {
    if (this.merging || this.inverting ||
      (this.currentStep === 0 && !this.selectSourceSubject) ||
      (this.currentStep === 1 && !this.selectTargetSubject && event.index > 1))
    {
      event.preventDefault();
    }
  }

  /**
   * Current step have been changed.
   *
   * @param stepIndex New step index.
   */
  public currentStepChange(stepIndex: number)
  {
    this.currentStep = stepIndex;
    if (this.currentStep === 0)
    {
      this.disabledToggleOnView = false;
    }
    else
      if (this.currentStep === 3)
      {
        this.memberService.hasOpenBatch(this.sourceSelector.selectedSubject.id).subscribe({
          next: (response) =>
          {
            this.hasOpenBatch = response;
          }
        });
      }
  }

  /**
   * Cancel merge confirmed by the user.
   *
   * @param cancelled If true, we are returning to step 1. If false, we just close the confirmation dialog.
   */
  public cancelMerge(cancelled: boolean): void
  {
    this.cancelDialogOpened = false;
    if (cancelled)
    {
      this.merging = false;
      this.mergeConfirmed = false;
      this.currentStep = 0;
      this.selectSourceSubject = null;
      this.selectTargetSubject = null;
      this.sourceSelector.clearSelection();
      this.targetSelector.clearSelection();
    }
  }

  /**
   * Event called when the merge is cancelled and asked for a confirmation.
   */
  public onCancelConfirmation(): void
  {
    this.cancelDialogOpened = true;
  }

  /**
   * Perfomr the subject merge operation.
   */
  public executeMerge(): void
  {
    this.merging = true;

    // Check if demographics override are selected
    let selectedProperties = [];
    if (this.sourceSelector.viewDemographics.data.length > 0)
    {
      const selectedDemo = this.sourceSelector.viewDemographics.data.filter(x => x.selected);
      const selectedExt = this.sourceSelector.viewExtensions.data.filter(x => x.selected);
      const selected = selectedDemo.concat(selectedExt);

      const resultArray = Object.keys(selected).map(index =>
      {
        let property;
        if (selected[index].key == "identifiers")
        {
          property = selected[index].value.substring(0, 2);
        }
        else
        {
          property = {}.hasOwnProperty.call(selected[index], 'settingExtension') ? selected[index].settingExtension.key : selected[index].key;
        }
        return property;
      });
      selectedProperties = resultArray;
    }

    const mergeRequest = {
      sourceMemberId: this.selectSourceSubject.id,
      targetMemberId: this.selectTargetSubject.id,
      targetMemberUpdateProperties: selectedProperties
    };

    this.memberService.mergeMember(mergeRequest).subscribe({
      next: () =>
      {
        this.translate.get('PATIENT-MERGE-COMPLETED').subscribe({
          next: (res) =>
          {
            this.toastrService.success(
              res.replace('{{patientLabel}}', this.patientLabelString), null,
              {
                enableHtml: true,
                timeOut: 5000
              })
              .onTap.subscribe({
                next: () =>
                {
                  this.openSubject(mergeRequest.targetMemberId);
                }
              });
          }
        });
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
        this.cancelMerge(true);
      },
      complete: () =>
      {
        this.cancelMerge(true);
      }
    });
  }

  /**
   * This method navigate to a specific Case Id
   *
   * @param subjectId Case Id to navigate to.
   */
  public openSubject(subjectId: number): void
  {
    this.router.navigate([`/patient/${subjectId}`]);
  }

  /**
   * Invert source and target subject
   */
  public invertSubject(): void
  {
    if (!this.inverting)
    {
      this.inverting = true;

      // Get Subject object selected
      const sourceSubject = cloneDeep(this.selectSourceSubject);
      const targetSubject = cloneDeep(this.selectTargetSubject);
      const sourceOperator = this.selectSourceOperator;
      const targetOperator = this.selectTargetOperator;

      // Set source subject
      this.selectSourceOperator = targetOperator;
      this.sourceSelector.changeFilterOperator(targetOperator);
      this.sourceSelector.handleSubjectFilterChange(targetSubject.fullName, targetSubject);
      this.selectSourceSubject = targetSubject;

      setTimeout(() =>
      {
        // Set target subject
        this.selectSourceOperator = sourceOperator;
        this.targetSelector.changeFilterOperator(sourceOperator);
        this.targetSelector.handleSubjectFilterChange(sourceSubject.fullName, sourceSubject);
        this.selectTargetSubject = sourceSubject;

        // Important: We must let the inversion to complete to prevent clicking too fast
        setTimeout(() => { this.inverting = false; }, 500);
      });
    }
  }

  /**
   * Load duplicated members with the same first/last name and date of birth.
   */
  public loadDuplicatedMembers(): void
  {
    this.selectedMemberKeys = [];
    this.duplicatedMemberCount = 0;
    this.viewDuplicatedMembers.setData([]);
    this.viewDuplicatedMembers.startLoading();
    this.memberService.getDuplicatedMembers().subscribe({
      next: (members) =>
      {
        members.forEach(member =>
        {
          if (member.dateOfBirth !== null && member.dateOfBirth !== undefined)
          {
            member.dateOfBirth = new Date(`${member.dateOfBirth}T12:00:00.000`);
          }
        });
        this.duplicatedMemberCount = members.length;
        this.viewDuplicatedMembers.setData(members);
        this.viewDuplicatedMembers.setSort([{ field: 'fullName', dir: 'asc' }]);
      },
      error: (error) =>
      {
        this.messageHandlerService.displayError(error);
      },
      complete: () =>
      {
        this.viewDuplicatedMembers.stopLoading();
      }
    });
  }

  /**
   * Event called when the tab is selected.
   *
   * @param event Select tab event
   */
  public onTabSelect(event: SelectEvent): void
  {
    this.selectedTab = event.index;
    if (this.selectedTab === 1)
    {
      this.loadDuplicatedMembers();
    }
    else if (this.selectedTab === 2)
    {
      this.loadMergesHistory();
    }
    else
    {
      this.currentStep = 0;
    }
  }

  /**
   * Data change event handler for the different views.
   *
   * @param view  View to change state for
   * @param state State of grid operations
   */
  public dataStateChange(state: DataStateChangeEvent): void
  {
    this.viewDuplicatedMembers.setState(state);
  }

  /**
   * This method is being called from selected duplicated subject.
   */
  public selectMembers(): void
  {
    if (this.selectedMemberKeys.length > 0 && this.selectedMemberKeys.length <= 2)
    {
      // Must select first tab before setting source and target
      this.tabstrip.selectTab(0);
      setTimeout(() =>
      {
        let index = 1;
        this.selectedMemberKeys.forEach((memberId) =>
        {
          const member: any = this.viewDuplicatedMembers.data.filter((x) => x.id === memberId);
          if (member.length === 1)
          {
            const selectedMember = member[0];
            if (index === 1)
            {
              // Setting source subject
              this.selectSourceSubject = selectedMember;
              this.sourceSelector.handleSubjectFilterChange(selectedMember.fullName, selectedMember);
              // If there is a second selection, we increase the step to set the target
              if (this.selectedMemberKeys.length === 2)
              {
                this.currentStep = this.currentStep + 1;
              }
            }
            else
            {
              this.disabledToggleOnView = true;
              setTimeout(() =>
              {
                // Setting target subject
                this.selectDefaultSubject = selectedMember;
                this.selectTargetSubject = selectedMember;
                this.targetSelector.handleSubjectFilterChange(selectedMember.fullName, selectedMember);
              });
            }
          }
          index += 1;
        });
      });
    }
  }

  /**
   * Number of duplicated selected members. Used to show the number of selected members in the selection button.
   *
   * @returns number Number of duplicated selected members
   */
  public getDuplicatedMembersSelectedCount(): number
  {
    return this.selectedMemberKeys.length;
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public historyDataStateChange(state: DataStateChangeEvent): void
  {
    this.historyState = state;
    this.executeHistoryQuery();
  }

  public searchFieldChange(filterText: string): void
  {
    this.historyState.skip = 0;
    const root = this.searchHelperService.fieldChange(filterText, this.historyState, 'targetMemberFullName');
    this.filterText = filterText;
    this.filterChange(root);
  }

  /**
   * Filtering by search field
   */
  public filterChange(filter: CompositeFilterDescriptor): void
  {
    this.historyState.filter = filter;
    this.executeHistoryQuery();
  }

  public onSelectedKeysChange(e: any): void
  {
    this.selectedMemberKeys = e;
  }

  private loadLocalizations(): void
  {
    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        this.patientLabel = { patientLabel: sessionSetting.patientLabel };
        this.patientLabelString = sessionSetting.patientLabel;
        this.translate.get('PATIENT-MERGE-STEPS').subscribe({
          next: (res: string) =>
          {
            const splitted = res.split(',');
            for (let i = 0; i < splitted.length; i++)
            {
              if (this.steps.length > i)
              {
                if (splitted[i].includes('{{patientLabel}}'))
                {
                  this.steps[i].label = splitted[i].replace('{{patientLabel}}', sessionSetting.patientLabel);
                }
                else
                {
                  this.steps[i].label = splitted[i];
                }
              }
            }
          }
        });
      }
    });
  }

  private loadMergesHistory(): void
  {
    this.historyState = {
      filter: {
        filters: [], logic: 'and'
      }, group: [], skip: 0,
      sort: [
        { field: 'createdOn', dir: 'desc' }
      ],
      take: this.appConfigService.getConfig().pageSize
    };
    this.executeHistoryQuery();
  }

  private executeHistoryQuery(): void
  {
    this.patientMergeHistoryListService.queryByState(this.historyState);
  }

}
